import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ICurrencyList,
  IIconStyles,
  ISalaryRange,
  ISalaryRangeOptions,
} from "./types/salaryRange";
import "./css/salaryRange.css";
import CountryFlag from "./components/CountryFlag";
import {
  currencies,
  debounce,
  formatNumberString,
  generateSalaryRange,
  getCurrencyBySiteContext,
  getUserDetails,
  unFormatNumberString,
} from "./utils/utils";
import Select from "./components/Select";
import Autocomplete from "./components/Autocomplete";

export interface IProps {
  salaryRange: ISalaryRange;
  handleUpdateSalaryRange: (newSalaryRange: ISalaryRange) => void;
  currencyList: ICurrencyList[];
  isRequired?: boolean;
  error?: string;
  gap?: string;
  labelStyles?: React.CSSProperties;
  iconStyles?: IIconStyles;
  onBlur?: () => void;
}

const SalaryRange: React.FC<IProps> = ({
  salaryRange,
  handleUpdateSalaryRange,
  currencyList,
  isRequired,
  error,
  gap,
  labelStyles,
  iconStyles,
  onBlur
}) => {
  const [selectedCurrency, setSelectedCurrency] =
    useState<ICurrencyList | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [showDropdown, setShowDropdown] = useState<string | null>(null);

  const { currencyListMap, currencyOptions } = useMemo(() => {
    const map: { [id: string]: ICurrencyList } = {};
    const options: { label: string; value: string; Icon: JSX.Element }[] = [];
    (currencyList?.length ? currencyList : currencies).forEach((item) => {
      map[item.id] = item;

      options.push({
        label: item.id,
        value: item.id,
        Icon: (
          <div
            style={{ marginRight: 1, display: "flex", alignItems: "center" }}
          >
            <CountryFlag value={item?.code} />
          </div>
        ),
      });
    });
    return { currencyListMap: map, currencyOptions: options };
  }, [currencyList]);

  const isIndianCurrency = selectedCurrency?.id === "INR";

  const salaries = useMemo(
    () => generateSalaryRange({ isIndianCurrency }),
    [isIndianCurrency]
  );

  const salaryList = useCallback(
    (isMin?: boolean) => {
      if(!isMin){
        const lastItem = salaries[salaries.length-1];
        return [...salaries, { label: `${lastItem.label}+`, value: `${lastItem.value}+` }]
      }

      return salaries;
    },
    [salaries]
  );

  const minSalaryList = useMemo(() => {
    if (salaryRange?.max) {
      return salaryList(true)?.filter(({ value }) => {
        const max = +unFormatNumberString(salaryRange?.max);
        const currentValue = +unFormatNumberString(value);

        return currentValue <= max;
      });
    }

    return salaryList(true);
  }, [salaryList, salaryRange?.max]);

  const maxSalaryList = useMemo(() => {
    return (
      salaryList()?.filter(({ value }) => {
        const min = +unFormatNumberString(salaryRange?.min);
        if (!min && !salaryRange?.min) {
          return true;
        }
        const currentValue = +unFormatNumberString(value);

        if (min >= 0 && min <= 450000) {
          return currentValue > min && currentValue <= min + 500000;
        } else if (min >= 500000 && min <= 1400000) {
          return currentValue > min && currentValue <= min + 1000000;
        } else if (min >= 1500000 && min <= 2900000) {
          return currentValue > min && currentValue <= min + 2000000;
        } else if (min >= 3000000) {
          return min >= 10000000 ? currentValue >= min : currentValue > min;
        }
        return false;
      }) || []
    );
  }, [salaryList, salaryRange?.min]);

  const handleBlur = (type: "min" | "max") => {
    setTimeout(() => {
      setSearchText("");
    }, 400);

    if(onBlur) onBlur();
  };

  const debouncedUpdateSalaryRange = useCallback(
    debounce((updatedSalaryRange: ISalaryRange) => {
      handleUpdateSalaryRange(updatedSalaryRange);
    }, 500),
    []
  );

  const handleInputChange = useCallback(
    (value: string, type: "min" | "max", isUpdatedFromProps?:boolean) => {
      
      const newRange: ISalaryRange = {
        ...salaryRange,
      };

      if (type === "min") {
        newRange.min = value;
        debouncedUpdateSalaryRange(newRange);
      } else {
        newRange.max = value;
        debouncedUpdateSalaryRange(newRange);
      }

      if(!isUpdatedFromProps){
        setSearchText(value);
      }
    },
    [isIndianCurrency, salaryRange]
  );

  const handleChangeMinSalary = (
    event: React.SyntheticEvent,
    newValue: ISalaryRangeOptions | null
  ) => {
    const formattedValue = formatNumberString({
      numStr: newValue?.value || "",
      sign: isIndianCurrency ? selectedCurrency?.sign || "" : "",
      isIndianCurrency,
      isMin: true,
    });

    const newRange: ISalaryRange = {
      ...salaryRange,
      min: newValue?.value || "",
    };
    handleUpdateSalaryRange(newRange);
    setShowDropdown(null);
    setSearchText("");
  };

  const handleChangeMaxSalary = (
    event: React.SyntheticEvent,
    newValue: ISalaryRangeOptions | null
  ) => {

    const newRange: ISalaryRange = {
      ...salaryRange,
      max: newValue?.value || "",
    };
    handleUpdateSalaryRange(newRange);
    setShowDropdown(null);
    setSearchText("");
  };

  const handleChangeCurrency = (newValue: string) => {
    const isPrevOrNewIsIndia =
      newValue === "INR" || salaryRange.currency === "INR";
    const newRange: ISalaryRange = {
      ...salaryRange,
      currency: newValue,
    };

    if (isPrevOrNewIsIndia) {
      newRange.min = "";
      newRange.max = "";
    }

    const selectedCurrencyItem = currencyListMap[newValue];
    handleUpdateSalaryRange(newRange);
    if (selectedCurrencyItem) {
      setSelectedCurrency(selectedCurrencyItem);
    }
  };

  const toggleDropdown = (value: "min" | "max" | null) => {
    setShowDropdown(value);
  };

  // Auto complete custom search
  const customFilter = (
    options: ISalaryRangeOptions[],
    state: { inputValue: string }
  ) => {
    const searchTerm = unFormatNumberString(
      state?.inputValue || "",
      selectedCurrency?.sign || ""
    );

    if (!searchTerm) {
      return options;
    }

    return options.filter((option) => {
      let formattedValue = option.value;

      formattedValue = unFormatNumberString(
        formattedValue || "",
        selectedCurrency?.sign || ""
      );

      return formattedValue.includes(searchTerm);
    });
  };

  useEffect(() => {
    const userData = getUserDetails();
    const defaultCurrency = getCurrencyBySiteContext(userData?.site_context);

    if (userData?.site_context) {
      const selectedCurrencyItem =
        currencyListMap[salaryRange.currency || defaultCurrency || "INR"];

      if (selectedCurrencyItem) {
        setSelectedCurrency(selectedCurrencyItem);
      }
      if (!salaryRange?.currency) {
        handleUpdateSalaryRange({
          currency: defaultCurrency || "INR",
          min: salaryRange.min || "",
          max: salaryRange.max ||  "",
        });
      }
    }
  }, [currencyListMap, salaryRange.currency]);


  return (
    <div id="salaryRange_salaryRangeContainer" style={{ gap, ...labelStyles }}>
      {/* Label */}
      <label className="salaryRange_salaryRangeLabel">
        Salary range {`${isIndianCurrency ? "(Annual)" : "(Monthly)"}`}{" "}
        <span className="salaryRange_required">{isRequired ? "*" : ""}</span>
      </label>

      <div
        className="salaryRange_salaryRangeRoot"
        style={{
          ...(!!error && {
            borderColor: "#FF0000",
          }),
        }}
      >
        {/* Select Currency  */}
        <Select
          onChange={handleChangeCurrency}
          options={currencyOptions}
          selectedOption={selectedCurrency?.id || salaryRange.currency}
          iconStyles={iconStyles}
        />

        {/* Min Salary  */}
        <Autocomplete
          customFilter={customFilter}
          fieldName="Min"
          handleChange={handleChangeMinSalary}
          handleInputChange={handleInputChange}
          isIndianCurrency={isIndianCurrency}
          options={minSalaryList}
          selectedCurrency={selectedCurrency}
          value={salaryRange.min}
          containerStyles={{
            borderRight: "1px solid #D2D1D6",
          }}
          showDropdown={showDropdown === "min"}
          toggleDropdown={toggleDropdown}
          handleBlur={handleBlur}
          iconStyles={iconStyles}
          searchText={searchText}
        />

        {/* Max Salary  */}
        <Autocomplete
          customFilter={customFilter}
          fieldName="Max"
          handleChange={handleChangeMaxSalary}
          handleInputChange={handleInputChange}
          isIndianCurrency={isIndianCurrency}
          options={maxSalaryList}
          selectedCurrency={selectedCurrency}
          value={salaryRange.max}
          inputWrapperStyles={{
            borderTopRightRadius: "4px",
            borderBottomRightRadius: "4px",
          }}
          showDropdown={showDropdown === "max"}
          toggleDropdown={toggleDropdown}
          handleBlur={handleBlur}
          iconStyles={iconStyles}
          searchText={searchText}
        />
      </div>
      {!!error && (
        <p className="salaryRange_error salaryRange_errorTxt">{error}</p>
      )}
    </div>
  );
};

export default SalaryRange;
